import { Box, Group, Stack, Text } from '@mantine/core';
import { Clock, Play } from 'react-feather';
import type {
  ReflectionGroupContext,
  ReflectionSessionDetails,
} from '../../../services/api/preSessionReflection';
import { ContentCard } from '../../../components/common/ContentCard';
import { SessionDetailsCard } from '../preSessionReflection/SessionDetailsCard';
import { YourGroupCard } from '../preSessionReflection/YourGroupCard';
import { AIListeningCard } from '../../cohorts/components/sessions/AIListeningCard';
import { SentimentGuidancePanel } from '../../cohorts/components/sessions/SentimentGuidancePanel';
import { StartSessionButton } from './StartSessionButton';
import { getZoneCode } from '../../../utils/formatters';

/** Mirror the coach Start-button gate (MED-1045): active from T-15 to T+1h. */
const READY_TO_START_WINDOW_MIN = 15;

// MED-1092 — readiness-banner tokens mirrored from the coach Group Readiness
// header (MED-1095) so the peer launch banner reads the same: light-blue when
// ready/live, neutral gray when scheduled; navy icon disc (Play) vs gray (Clock).
const READY_BANNER_BG = '#EBF2FA';
const SCHEDULED_BANNER_BG = '#F3F4F6';
const READY_ICON_BG = '#203358';
const SCHEDULED_ICON_BG = '#E5E7EB';
const SCHEDULED_ICON_COLOR = '#6B7280';
const BANNER_TITLE_COLOR = '#1A1A2E';

function isInReadyWindow(scheduledStart: string | null): boolean {
  if (!scheduledStart) return false;
  const start = new Date(scheduledStart).getTime();
  if (Number.isNaN(start)) return false;
  const now = Date.now();
  return (
    now >= start - READY_TO_START_WINDOW_MIN * 60_000 &&
    now < start + 60 * 60_000
  );
}

interface PeerLeaderLaunchCardProps {
  sessionId: string;
  cohortId: string;
  groupId: string | null;
  groupName: string | null;
  scheduledStart: string | null;
  sessionStatus: string;
  aiFeaturesEnabled: boolean;
  /** AI features ON *and* listening ON — gates the Sentiment & Guidance panel. */
  aiSurfacesEnabled: boolean;
  aiListeningEnabled: boolean;
  aiListeningLocked: boolean;
  onToggleAiListening: (
    next: boolean
  ) => Promise<{ ai_listening_enabled: boolean; ai_listening_locked: boolean }>;
  /** From the reflection payload — null when reflection didn't load. */
  group: ReflectionGroupContext | null;
  sessionDetails: ReflectionSessionDetails | null;
}

/**
 * MED-1049 (E4.2b) Card 5 — Launch / group card.
 *
 * Mirrors the coach prep group card (`CoachPrepGroupSection`) in look and
 * ORDER, but composed from the leaf components the peer-leader surface already
 * uses so it stays on the peer-leader data shape (no coach multi-group prep):
 *
 *   1. Status row — Scheduled / Ready to start / Live now + Start Session button
 *      (the button self-gates to the T-15 window per MED-1045 / MED-816).
 *   2. Roster — `YourGroupCard` (group members from the reflection payload).
 *   3. AI Listening card (when AI features are on), then Session Details card.
 *   4. Sentiment & Guidance panel — only when AI listening is on (anonymized).
 *
 * The coach "Before you start" checklist and the multi-group tab strip are
 * intentionally omitted (AC9): a peer leader facilitates a single group and the
 * step accordions above serve the checklist's purpose, freeing space for the
 * Session Details card.
 */
export function PeerLeaderLaunchCard({
  sessionId,
  cohortId,
  groupId,
  groupName,
  scheduledStart,
  sessionStatus,
  aiFeaturesEnabled,
  aiSurfacesEnabled,
  aiListeningEnabled,
  aiListeningLocked,
  onToggleAiListening,
  group,
  sessionDetails,
}: PeerLeaderLaunchCardProps) {
  const start = scheduledStart ? new Date(scheduledStart) : null;
  const daysAway =
    start && !Number.isNaN(start.getTime())
      ? Math.max(0, Math.ceil((start.getTime() - Date.now()) / 86_400_000))
      : null;

  const statusText =
    sessionStatus === 'in_progress'
      ? 'Live now'
      : isInReadyWindow(scheduledStart)
        ? 'Ready to start'
        : 'Scheduled';

  // MED-1389: name the zone, as the coach view does.
  const subline =
    start && !Number.isNaN(start.getTime())
      ? `${start.toLocaleString(undefined, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })} at ${start.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        })} ${getZoneCode(null, start)}${daysAway !== null ? ` · ${daysAway} ${daysAway === 1 ? 'day' : 'days'} away` : ''}`
      : null;

  const groupLabel = groupName ?? 'Your group';
  const isReady = statusText !== 'Scheduled';

  return (
    // MED-1092 — elevated ContentCard to match the coach prep group section.
    // padding=0 + overflow:hidden so the readiness banner is the card header,
    // full-width and flush to the (rounded) top edge — mirroring the coach
    // GroupStatusRow; the body below carries its own padding.
    <ContentCard
      variant="elevated"
      radius={16}
      padding={0}
      contentGap={0}
      fill={false}
      // MED-1092 — a 1px border (on top of the elevated shadow) gives the
      // full-bleed readiness banner a crisp left/right edge so it reads against
      // the page background instead of blending into it.
      style={{ overflow: 'hidden', border: '1px solid #E5E7EB' }}
      header={
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          style={{
            padding: 'var(--mantine-spacing-md) var(--mantine-spacing-lg)',
            background: isReady ? READY_BANNER_BG : SCHEDULED_BANNER_BG,
          }}
        >
          <Group gap="sm" align="center" wrap="nowrap">
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: isReady ? READY_ICON_BG : SCHEDULED_ICON_BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
              aria-hidden
            >
              {isReady ? (
                <Play size={16} color="#FFFFFF" fill="#FFFFFF" />
              ) : (
                <Clock size={16} color={SCHEDULED_ICON_COLOR} />
              )}
            </div>
            <Stack gap={2}>
              <Text fw={600} c={BANNER_TITLE_COLOR}>
                {groupLabel} — {statusText}
              </Text>
              {subline && (
                <Text size="sm" c="dimmed">
                  {subline}
                </Text>
              )}
            </Stack>
          </Group>
          <StartSessionButton
            sessionId={sessionId}
            cohortId={cohortId}
            scheduledStart={scheduledStart}
            sessionStatus={sessionStatus}
            groupName={groupName}
            aiFeaturesEnabled={aiFeaturesEnabled}
          />
        </Group>
      }
    >
      {/* Padded body below the full-width banner. */}
      <Box p="lg">
        <Stack gap="md">
          {/* Two-column body — mirror the coach prep group card
              (CoachPrepGroupSection): roster on the left, a fixed-width right
              rail stacking AI Listening then Session Details. The shared cards
              take radius={16} here to match the coach roster (their default md
              keeps the participant pre-session page unchanged). */}
          <Group align="flex-start" gap="md" wrap="wrap">
            <Box style={{ flex: '1 1 480px' }}>
              {group && <YourGroupCard group={group} radius={16} />}
            </Box>
            <Box style={{ flex: '0 0 320px' }}>
              <Stack gap="md">
                {aiFeaturesEnabled && (
                  <AIListeningCard
                    sessionId={sessionId}
                    enabled={aiListeningEnabled}
                    locked={aiListeningLocked}
                    onToggle={onToggleAiListening}
                  />
                )}
                {sessionDetails && (
                  <SessionDetailsCard details={sessionDetails} radius={16} />
                )}
              </Stack>
            </Box>
          </Group>

          {/* Sentiment & Guidance — only when AI listening is on (anonymized) */}
          {aiSurfacesEnabled && groupId && (
            <SentimentGuidancePanel
              sessionId={sessionId}
              groupId={groupId}
              groupName={groupLabel}
              anonymizeSources
            />
          )}
        </Stack>
      </Box>
    </ContentCard>
  );
}

export default PeerLeaderLaunchCard;
